import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DownloadSimple, Funnel, ArrowLeft, CursorClick, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import DateRangePresets, { presetToRange } from "@/components/DateRangePresets";
import BrandCombobox from "@/components/BrandCombobox";
import FeedPartnerSelect, { appendNetworkParam } from "@/components/FeedPartnerSelect";
import NetworkBadge from "@/pages/publisher/pub_brands/NetworkBadge";
import { Skeleton } from "@/components/ui/skeleton";

function downloadCSV(data, columns, filename) {
  const header = columns.map(c => c.label).join(",");
  const rows = data.map(row => columns.map(c => {
    const val = row[c.key]; const str = val == null ? "" : String(val);
    return str.includes(",") ? `"${str}"` : str;
  }).join(","));
  const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

function fmtInt(n) {
  return Number(n || 0).toLocaleString();
}

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_TILE_TONES = {
  primary: {
    card: "bg-[#002FA7] border-[#002FA7] text-white",
    label: "text-white/70",
    value: "text-white",
    sub: "text-white/60",
  },
  pending: {
    card: "bg-amber-50 border-amber-200",
    label: "text-amber-700",
    value: "text-amber-900",
    sub: "text-amber-700/70",
  },
  rejected: {
    card: "bg-red-50 border-red-200",
    label: "text-red-700",
    value: "text-red-800",
    sub: "text-red-700/70",
  },
  neutral: {
    card: "bg-white border-slate-200",
    label: "text-slate-500",
    value: "text-slate-900",
    sub: "text-slate-500",
  },
};

/** Status earning tile — same look as Payouts Approved / Pending / Rejected. */
function StatusEarningCard({ label, value, sub, tone = "neutral", loading, testId, active, onClick }) {
  const t = STATUS_TILE_TONES[tone] || STATUS_TILE_TONES.neutral;
  const ringClass = active
    ? (tone === "primary"
      ? "ring-2 ring-offset-2 ring-white/90 shadow-lg"
      : tone === "pending"
        ? "ring-2 ring-offset-2 ring-amber-500"
        : tone === "rejected"
          ? "ring-2 ring-offset-2 ring-red-500"
          : "ring-2 ring-offset-2 ring-slate-400")
    : "";
  const interactive = onClick ? "cursor-pointer transition-all hover:opacity-90 active:scale-[0.98]" : "";
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`border rounded-md p-3 sm:p-4 min-w-0 overflow-hidden text-left w-full ${t.card} ${ringClass} ${interactive}`}
      data-testid={testId}
      aria-pressed={onClick ? !!active : undefined}
    >
      <div className={`text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-bold mb-1 truncate ${t.label}`}>
        {label}
      </div>
      {loading ? (
        <Skeleton className="h-7 w-24 bg-slate-200/60" />
      ) : (
        <>
          <div
            className={`text-base sm:text-lg font-bold font-mono tabular-nums leading-tight break-all ${t.value}`}
            title={value}
          >
            {value}
          </div>
          {sub != null && sub !== "" && (
            <div className={`text-[10px] font-mono mt-1 ${t.sub}`}>{sub}</div>
          )}
        </>
      )}
    </Wrapper>
  );
}

function StatCard({ label, value, accent, loading }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 sm:p-4 min-w-0 overflow-hidden bg-white">
      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 truncate">{label}</div>
      {loading ? (
        <Skeleton className="h-7 w-28 bg-slate-200" />
      ) : (
        <div
          className={`text-base sm:text-lg font-bold font-mono tabular-nums leading-tight break-all ${accent ? "text-[#002FA7]" : "text-slate-900"}`}
          title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
        >
          {value}
        </div>
      )}
    </div>
  );
}

function TableSkeleton({ cols = 7, rows = 8 }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex gap-4">
        {Array.from({ length: Math.min(cols, 5) }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-20 bg-slate-200" />
        ))}
      </div>
      <div className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-4 py-3 flex gap-4">
            {Array.from({ length: Math.min(cols, 6) }).map((_, j) => (
              <Skeleton key={j} className="h-4 flex-1 bg-slate-100" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── BRAND DETAIL VIEW ───
function BrandClickDetail({ brandName, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await API.get(`/publisher/reports/click-level/${encodeURIComponent(brandName)}`);
        setData(d);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [brandName]);

  if (loading) return <div className="text-sm text-slate-400 py-8">Loading brand details...</div>;
  if (!data?.published) return <div className="text-sm text-slate-400 py-8">Reports not available</div>;

  const oc = data.overall_clicks || {};
  const yc = data.your_clicks;
  const conversions = data.conversions || [];
  const totalEarning = conversions.reduce((s, c) => s + c.earning, 0);
  const totalCommission = conversions.reduce((s, c) => s + (c.pub_commission || 0), 0);

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors" data-testid="back-to-click-list">
        <ArrowLeft size={16} /> Back to All Brands
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900">{data.brand_name}</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Commission: <span className="font-mono text-[#002FA7] font-bold">{data.commission_pct}%</span>
            {data.status && <span className={`ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${data.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{data.status}</span>}
          </p>
        </div>
        {conversions.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => downloadCSV(conversions, [
            {key:"action_id",label:"Action ID"},{key:"state",label:"Status"},{key:"amount",label:"Amount"},
            {key:"pub_commission",label:"Total Commission"},{key:"earning",label:"Your Earning"},
            {key:"event_date",label:"Date"},{key:"order_id",label:"Order ID"},{key:"promo_code",label:"Promo"}
          ], `${data.brand_name}_conversions.csv`)} data-testid="download-brand-csv">
            <DownloadSimple size={16} className="mr-1.5" /> Download CSV
          </Button>
        )}
      </div>

      {/* Overall Clicks (Account Level) */}
      <div className="border border-slate-200 rounded-md p-5 mb-4">
        <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Overall Brand Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Clicks" value={oc.clicks?.toLocaleString() || 0} />
          <StatCard label="Actions" value={oc.actions || 0} />
          <StatCard label="Impressions" value={oc.impressions || 0} />
          <StatCard label="Sale Amount" value={`$${(oc.sale_amount || 0).toFixed(2)}`} />
          <StatCard label="CR" value={parseFloat(oc.cr || 0).toFixed(4)} />
          <StatCard label="EPC" value={parseFloat(oc.epc || 0).toFixed(4)} />
        </div>
      </div>

      {/* Your SubID Clicks */}
      {yc && (
        <div className="border border-[#002FA7]/20 bg-[#002FA7]/5 rounded-md p-5 mb-4">
          <h3 className="text-xs uppercase tracking-wider text-[#002FA7] font-bold mb-3">Your Clicks (SubID Tracked)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Raw Clicks" value={yc.raw_clicks?.toLocaleString() || 0} accent />
            <StatCard label="Unique Clicks" value={yc.clicks?.toLocaleString() || 0} accent />
            <StatCard label="Actions" value={yc.actions || 0} accent />
            <StatCard label="Sale Amount" value={`$${(yc.sale_amount || 0).toFixed(2)}`} accent />
          </div>
        </div>
      )}

      {/* Conversions Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Conversions ({conversions.length})</h3>
          <span className="text-xs font-mono text-[#002FA7] font-bold">
            Total Commission: ${totalCommission.toFixed(2)} · Your Earning: ${totalEarning.toFixed(2)}
          </span>
        </div>
        {conversions.length > 0 ? (
          <div className="max-h-[400px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action ID</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Sale Amount</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Total Commission</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Your Earning</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Date</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Order ID</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Promo</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map((c, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{c.action_id}</td>
                    <td className="px-4 py-2.5"><StatusPill status={c.state} size="md" /></td>
                    <td className="px-4 py-2.5 text-right font-mono">${c.amount?.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-700">${(c.pub_commission || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-[#002FA7]">${c.earning?.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{c.event_date?.substring(0, 10)}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{c.order_id || "-"}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{c.promo_code || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No approved conversions for this brand yet</div>
        )}
      </div>
    </div>
  );
}

// MAIN REPORTS PAGE
// Default date range is "Last 7 days" — keeps the page fast on
// large publishers (no full-history scan on every visit). "Clear"
// snaps back to this preset, not to "All time", so the table never
// shows an empty state after a filter reset.
const DEFAULT_PRESET = "last_7";
const initialRange = () => {
  const r = presetToRange(DEFAULT_PRESET);
  return { preset: DEFAULT_PRESET, start: r.start, end: r.end };
};

export default function PubReports() {
  const { user } = useAuth();
  const accountTimezone = user?.timezone || "UTC";
  const discloseNetworks = Boolean(user?.disclose_networks);
  const [clickData, setClickData] = useState(null);
  const [commData, setCommData] = useState(null);
  const [loadingClicks, setLoadingClicks] = useState(false);
  const [loadingComm, setLoadingComm] = useState(false);
  const [clickBrand, setClickBrand] = useState("all");
  const [networkFilter, setNetworkFilter] = useState("all");
  const [commBrand, setCommBrand] = useState("all");
  const [commStatus, setCommStatus] = useState(null);
  const [published, setPublished] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [convLogData, setConvLogData] = useState(null);
  const [loadingConvLog, setLoadingConvLog] = useState(false);
  const [convLogBrand, setConvLogBrand] = useState("all");
  // ONE date range for every tab.
  //
  // These were four independent `useState(initialRange)` values — one per
  // tab — all defaulting to `last_7`. Setting "All time" on Click Level
  // left Click Log on its own untouched default, so adjacent tabs showed
  // DIFFERENT PERIODS with nothing on screen saying so. Observed on prod:
  // Click Log reading 60,480 clicks / 1,293 sales next to Click Level's
  // 1,300,726 / 42,224 for the same publisher — not a data discrepancy,
  // just seven days sitting beside all-time.
  //
  // Aliased rather than renamed at all 54 call sites: the four names keep
  // working, and there is now exactly one source of truth behind them.
  const [reportRange, setReportRange] = useState(initialRange);
  const convLogRange = reportRange;
  const setConvLogRange = setReportRange;
  const clickRange = reportRange;
  const setClickRange = setReportRange;
  const commRange = reportRange;
  const setCommRange = setReportRange;
  const payoutRange = reportRange;
  const setPayoutRange = setReportRange;

  const [subIdFilter, setSubIdFilter] = useState("");
  // CSV pre-flight estimates — { rows, tier, is_blocked } per report tab.
  const [commCsvEstimate, setCommCsvEstimate] = useState(null);
  const [payoutCsvEstimate, setPayoutCsvEstimate] = useState(null);
  // Report list pagination — default 20 / max 100 (matches API clamp).
  const REPORT_PAGE_SIZE = 20;
  const [activeTab, setActiveTab] = useState("click-level");
  const [clickLogPage, setClickLogPage] = useState(1);
  const [clickLogTotal, setClickLogTotal] = useState(0);
  const [clickLevelPage, setClickLevelPage] = useState(1);
  const [commPage, setCommPage] = useState(1);
  const [earningsPage, setEarningsPage] = useState(1);

  useEffect(() => {
    if (!discloseNetworks) setNetworkFilter("all");
  }, [discloseNetworks]);

  const fetchClickLog = useCallback(async (brand, startStr, endStr, subId2Filter, pageNum = 1) => {
    setLoadingConvLog(true);
    // Hide prior totals/rows while filters reload so stale numbers don't linger.
    if (pageNum === 1) setConvLogData(null);
    try {
      const params = new URLSearchParams();
      if (brand && brand !== "all") params.set("brand", brand);
      if (startStr) params.set("start_date", startStr);
      if (endStr) params.set("end_date", endStr);
      if (subId2Filter) params.set("sub_id2", subId2Filter);
      appendNetworkParam(params, networkFilter, discloseNetworks);
      params.set("page", String(pageNum));
      params.set("page_size", String(REPORT_PAGE_SIZE));

      const { data } = await API.get(`/publisher/click-log?${params.toString()}`);
      setClickLogTotal(data?.total || 0);
      setConvLogData((prev) => {
        const nextTotals = data?.totals;
        const keepPrev =
          pageNum > 1
          && prev?.totals
          && (!nextTotals || !(nextTotals.total_sales || nextTotals.approved_earning || nextTotals.pending_earning || nextTotals.total_earnings));
        return {
          data: data?.data || [],
          brands: data?.brands || [],
          published: true,
          total: data?.total || 0,
          totals: keepPrev ? prev.totals : (nextTotals || null),
        };
      });
    } catch (err) { console.error(err); }
    finally { setLoadingConvLog(false); }
  }, [networkFilter, discloseNetworks]);

  const fetchClickData = useCallback(async (brand, startDate, endDate, pageNum = 1) => {
    setLoadingClicks(true);
    if (pageNum === 1) setClickData(null);
    try {
      const params = new URLSearchParams();
      if (brand && brand !== "all") params.set("brand", brand);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      appendNetworkParam(params, networkFilter, discloseNetworks);
      params.set("page", String(pageNum));
      params.set("page_size", String(REPORT_PAGE_SIZE));
      const { data } = await API.get(`/publisher/reports/click-level?${params.toString()}`);
      setClickData(data);
      setPublished(data.published);
    } catch (err) { console.error(err); }
    finally { setLoadingClicks(false); }
  }, [networkFilter, discloseNetworks]);

  const fetchCommData = useCallback(async (brand, startDate, endDate, pageNum = 1, status = null) => {
    setLoadingComm(true);
    if (pageNum === 1) setCommData(null);
    try {
      const params = new URLSearchParams();
      if (brand && brand !== "all") params.set("brand", brand);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (status) params.set("status", status);
      appendNetworkParam(params, networkFilter, discloseNetworks);
      params.set("page", String(pageNum));
      params.set("page_size", String(REPORT_PAGE_SIZE));
      const { data } = await API.get(`/publisher/reports/commission-level?${params.toString()}`);
      setCommData((prev) => {
        // Page flips omit totals — keep page-1 StatCards.
        if (pageNum > 1 && prev?.totals && !data?.totals) {
          return { ...data, totals: prev.totals };
        }
        return { ...data, totals: data?.totals || null };
      });
      setPublished(data.published);
    } catch (err) { console.error(err); }
    finally { setLoadingComm(false); }
  }, [networkFilter, discloseNetworks]);

  const searchReportBrands = useCallback(async (q) => {
    const params = new URLSearchParams({ q, limit: "30" });
    const { data } = await API.get(`/publisher/reports/brands/search?${params}`);
    return data?.brands || [];
  }, []);

  // Lazy-load: only hit the active tab's API. Loading all four tabs at
  // once was stacking 15–30s Mongo scans on every Reports visit.
  useEffect(() => { setClickLevelPage(1); }, [clickBrand, clickRange, networkFilter]);
  useEffect(() => {
    if (activeTab !== "click-level") return;
    fetchClickData(clickBrand, clickRange.start, clickRange.end, clickLevelPage);
  }, [activeTab, clickBrand, clickRange, clickLevelPage, networkFilter, fetchClickData]);
  useEffect(() => { setCommPage(1); }, [commBrand, commRange, commStatus, networkFilter]);
  useEffect(() => {
    if (activeTab !== "commission-level") return;
    fetchCommData(commBrand, commRange.start, commRange.end, commPage, commStatus);
  }, [activeTab, commBrand, commRange, commPage, commStatus, networkFilter, fetchCommData]);

  // Reset to page 1 when filters change, then fetch.
  useEffect(() => { setClickLogPage(1); }, [convLogBrand, convLogRange.start, convLogRange.end, subIdFilter, networkFilter]);
  useEffect(() => {
    if (activeTab !== "conversion-log") return;
    fetchClickLog(convLogBrand, convLogRange.start, convLogRange.end, subIdFilter, clickLogPage);
  }, [activeTab, convLogBrand, convLogRange.start, convLogRange.end, subIdFilter, clickLogPage, networkFilter, fetchClickLog]);

  // ─── Earnings Breakdown (Payouts) ───
  const [earningsData, setEarningsData] = useState(null);
  const [earningsBrand, setEarningsBrand] = useState("all");
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  const fetchEarnings = useCallback(async (brand = "all", startDate = "", endDate = "", pageNum = 1) => {
    setLoadingEarnings(true);
    // Hide prior rows on filter reload; keep StatCards / rollups on page flips.
    if (pageNum === 1) setEarningsData(null);
    try {
      const params = new URLSearchParams();
      if (brand && brand !== "all") params.set("brand", brand);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      appendNetworkParam(params, networkFilter, discloseNetworks);
      params.set("page", String(pageNum));
      params.set("page_size", String(REPORT_PAGE_SIZE));
      const { data } = await API.get(`/publisher/reports/earnings-breakdown?${params.toString()}`);
      setEarningsData((prev) => {
        const nextTotals = data?.totals;
        const keepPrev =
          pageNum > 1
          && prev?.totals
          && (!nextTotals || !(nextTotals.approved_earning || nextTotals.pending_earning || nextTotals.total_sale_amount));
        return {
          ...data,
          totals: keepPrev ? prev.totals : (nextTotals || null),
          by_date: (pageNum > 1 && prev?.by_date?.length) ? prev.by_date : (data?.by_date || []),
          by_brand: (pageNum > 1 && prev?.by_brand?.length) ? prev.by_brand : (data?.by_brand || []),
        };
      });
    } catch (err) { console.error(err); }
    finally { setLoadingEarnings(false); }
  }, [networkFilter, discloseNetworks]);

  useEffect(() => { setEarningsPage(1); }, [earningsBrand, payoutRange, networkFilter]);
  useEffect(() => {
    if (activeTab !== "earnings-breakdown") return;
    fetchEarnings(earningsBrand, payoutRange.start, payoutRange.end, earningsPage);
  }, [activeTab, earningsBrand, payoutRange, earningsPage, networkFilter, fetchEarnings]);

  // ─── CSV pre-flight estimates ─────────────────────────────────
  // Hits /publisher/reports/export-estimate to get row count + tier
  // BEFORE the pub clicks download. Avoids the "downloads 0 rows
  // silently" surprise — the button shows the row count up-front
  // and disables itself if the export would be hard-rejected
  // (> 5M rows).
  useEffect(() => {
    if (activeTab !== "commission-level") return;
    let cancelled = false;
    const params = new URLSearchParams();
    params.set("report", "commission_level");
    if (commBrand && commBrand !== "all") params.set("brand", commBrand);
    if (commRange.start) params.set("start_date", commRange.start);
    if (commRange.end) params.set("end_date", commRange.end);
    if (commStatus) params.set("status", commStatus);
    appendNetworkParam(params, networkFilter, discloseNetworks);
    API.get(`/publisher/reports/export-estimate?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setCommCsvEstimate(data); })
      .catch(() => { if (!cancelled) setCommCsvEstimate(null); });
    return () => { cancelled = true; };
  }, [activeTab, commBrand, commRange, commStatus, networkFilter]);

  useEffect(() => {
    if (activeTab !== "earnings-breakdown") return;
    let cancelled = false;
    const params = new URLSearchParams();
    params.set("report", "payouts");
    if (earningsBrand && earningsBrand !== "all") params.set("brand", earningsBrand);
    if (payoutRange.start) params.set("start_date", payoutRange.start);
    if (payoutRange.end) params.set("end_date", payoutRange.end);
    appendNetworkParam(params, networkFilter, discloseNetworks);
    API.get(`/publisher/reports/export-estimate?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setPayoutCsvEstimate(data); })
      .catch(() => { if (!cancelled) setPayoutCsvEstimate(null); });
    return () => { cancelled = true; };
  }, [activeTab, earningsBrand, payoutRange, networkFilter]);

  const downloadStreamingCsv = async (endpoint, params, fallbackFilename) => {
    // Hit the backend streaming export — pulls every matching row
    // server-side and writes the CSV (or ZIP, for big ranges) to a
    // blob. Avoids OOM on the client and pulls the full result set
    // (no client-side cap).
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${endpoint}?${qs.toString()}`;
    const resp = await fetch(url, {
      credentials: "include",
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try { const j = await resp.json(); msg = j.detail || msg; } catch { /* not JSON, keep default */ }
      // 413 = hard reject (> 5M rows). Surface the helpful message
      // verbatim — it tells the pub to narrow the date range.
      alert(msg);
      return;
    }
    const blob = await resp.blob();
    const dispositionHeader = resp.headers.get("content-disposition") || "";
    const matched = /filename="([^"]+)"/.exec(dispositionHeader);
    const filename = matched ? matched[1] : fallbackFilename;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename; a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const exportEarningsCsv = () => downloadStreamingCsv(
    "/publisher/reports/earnings-breakdown/export",
    {
      brand: earningsBrand !== "all" ? earningsBrand : "",
      network: discloseNetworks && networkFilter !== "all" ? networkFilter : "",
      start_date: payoutRange.start, end_date: payoutRange.end,
    },
    `payouts-${new Date().toISOString().slice(0, 10)}.csv`,
  );
  const exportCommissionCsv = () => downloadStreamingCsv(
    "/publisher/reports/commission-level/export",
    {
      brand: commBrand !== "all" ? commBrand : "",
      network: discloseNetworks && networkFilter !== "all" ? networkFilter : "",
      start_date: commRange.start || "",
      end_date: commRange.end || "",
      status: commStatus || "",
    },
    `commission_report-${new Date().toISOString().slice(0, 10)}.csv`,
  );

  const exportClickLevelCsv = () => downloadStreamingCsv(
    "/publisher/reports/click-level/export",
    {
      brand: clickBrand !== "all" ? clickBrand : "",
      network: discloseNetworks && networkFilter !== "all" ? networkFilter : "",
      start_date: clickRange.start || "",
      end_date: clickRange.end || "",
    },
    `click_report-${new Date().toISOString().slice(0, 10)}.csv`,
  );

  // ─── Click Log CSV — streaming export with progress ────────────────
  // Previously: client-side downloadCSV on a 50k-page-size fetch which
  // silently truncated the user's CSV at 50k rows ($7k vs UI's $99k).
  // Now: hit the streaming /publisher/click-log/export.csv endpoint
  // which iterates the cursor directly with no row cap.
  const [clickLogCsvExporting, setClickLogCsvExporting] = useState(false);
  const [clickLogCsvProgress, setClickLogCsvProgress] = useState({ received: 0, elapsedMs: 0, total: 0 });

  const downloadClickLogCsv = async () => {
    if (clickLogCsvExporting) return;
    const startedAt = Date.now();
    setClickLogCsvExporting(true);
    setClickLogCsvProgress({ received: 0, elapsedMs: 0, total: 0 });
    const ticker = setInterval(() => {
      setClickLogCsvProgress((s) => ({ ...s, elapsedMs: Date.now() - startedAt }));
    }, 250);

    try {
      const params = new URLSearchParams();
      if (convLogBrand && convLogBrand !== "all") params.set("brand", convLogBrand);
      if (convLogRange.start) params.set("start_date", convLogRange.start);
      if (convLogRange.end) params.set("end_date", convLogRange.end);
      if (subIdFilter) params.set("sub_id2", subIdFilter);
      appendNetworkParam(params, networkFilter, discloseNetworks);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/publisher/click-log/export.csv?${params.toString()}`;
      const token = localStorage.getItem("token") || "";
      const resp = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try { const j = await resp.json(); msg = j.detail || msg; } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const totalHdr = parseInt(resp.headers.get("X-Total-Count") || "0", 10);
      setClickLogCsvProgress((s) => ({ ...s, total: totalHdr }));

      // Progressive read so the progress card updates as the stream
      // arrives — important for multi-hundred-thousand-row exports.
      const reader = resp.body.getReader();
      const chunks = [];
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setClickLogCsvProgress((s) => ({ ...s, received }));
      }
      const blob = new Blob(chunks, { type: "text/csv" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `click_log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(
        totalHdr > 0
          ? `Exported up to ${totalHdr.toLocaleString()} click${totalHdr === 1 ? "" : "s"} to CSV`
          : "Click log exported to CSV",
      );
    } catch (err) {
      console.error("CSV export failed", err);
      toast.error(err.message || "CSV export failed");
    } finally {
      clearInterval(ticker);
      setClickLogCsvExporting(false);
    }
  };

  const clickRows = clickData?.data || [];
  const subidRows = clickData?.subid_data || [];
  const commRows = commData?.data || [];

  const clickTotals = clickData?.totals || null;
  const clickTotalClicks = clickTotals?.clicks ?? clickRows.reduce((s, r) => s + (r.clicks || 0), 0);
  const clickTotalActions = clickTotals?.actions ?? clickRows.reduce((s, r) => s + (r.actions || 0), 0);
  const clickTotalSaleAmount = clickTotals?.sale_amount ?? clickRows.reduce((s, r) => s + (r.sale_amount || 0), 0);
  const clickBrandCount = clickTotals?.brands ?? (clickData?.total || clickRows.length);
  const clickApprovedEarning = clickTotals?.approved_earning ?? 0;
  const clickPendingEarning = clickTotals?.pending_earning ?? 0;
  const clickRejectedEarning = clickTotals?.rejected_earning ?? 0;
  const clickApprovedActions = clickTotals?.approved_actions ?? 0;
  const clickPendingActions = clickTotals?.pending_actions ?? 0;
  const clickRejectedActions = clickTotals?.rejected_actions ?? 0;

  const commTotals = commData?.totals || null;
  const commTotalAmount = commTotals?.sale_amount ?? commRows.reduce((s, r) => s + (r.amount || 0), 0);
  const commActionCount = commTotals?.actions ?? (commData?.total || commRows.length);
  const commApprovedEarning = commTotals?.approved_earning ?? 0;
  const commPendingEarning = commTotals?.pending_earning ?? 0;
  const commRejectedEarning = commTotals?.rejected_earning ?? 0;
  const commApprovedActions = commTotals?.approved_actions ?? 0;
  const commPendingActions = commTotals?.pending_actions ?? 0;
  const commRejectedActions = commTotals?.rejected_actions ?? 0;

  const toggleCommStatus = (status) => {
    setCommStatus((prev) => (prev === status ? null : status));
  };

  const clickLevelTotal = clickData?.total || 0;
  const clickLevelPages = Math.max(1, clickData?.total_pages || Math.ceil(clickLevelTotal / REPORT_PAGE_SIZE) || 1);
  const clickLevelPageSafe = Math.min(clickLevelPage, clickLevelPages);

  const commTotal = commData?.total || 0;
  const commPages = Math.max(1, commData?.total_pages || Math.ceil(commTotal / REPORT_PAGE_SIZE) || 1);
  const commPageSafe = Math.min(commPage, commPages);

  const earningsTotal = earningsData?.total || 0;
  const earningsPages = Math.max(1, earningsData?.total_pages || Math.ceil(earningsTotal / REPORT_PAGE_SIZE) || 1);
  const earningsPageSafe = Math.min(earningsPage, earningsPages);

  // SubID panel is useful for a single-brand drill-in; on All Brands it
  // duplicated the main table and froze the page with thousands of rows.
  const showSubidPanel = clickBrand !== "all" && subidRows.length > 0;

  const commColumns = [
    {key:"campaign_name",label:"Brand"},{key:"state",label:"State"},{key:"amount",label:"Amount"},
    {key:"commission_pct",label:"Commission %"},{key:"pub_commission",label:"Total Commission"},{key:"earning",label:"Your Earning"},{key:"event_date",label:"Date"},
    {key:"promo_code",label:"Promo"},{key:"order_id",label:"Order ID"},
  ];

  return (
    <div className="p-8" data-testid="publisher-reports-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Analytics / Performance
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Reports</h1>
        <p className="text-sm text-slate-500 mt-2">Click-level and commission-level reporting</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="border-b border-slate-200 bg-transparent h-auto p-0 mb-6 rounded-none">
          <TabsTrigger value="click-level" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="pub-tab-click-level">Click Level Reports</TabsTrigger>
          <TabsTrigger value="commission-level" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="pub-tab-commission-level">Commission Level Reports</TabsTrigger>
          <TabsTrigger value="conversion-log" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="pub-tab-conversion-log">Click Log</TabsTrigger>
          <TabsTrigger value="earnings-breakdown" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="pub-tab-earnings-breakdown">Payouts</TabsTrigger>
        </TabsList>

        {/* ═══ CLICK LEVEL ═══ */}
        <TabsContent value="click-level">
          {selectedBrand ? (
            <BrandClickDetail brandName={selectedBrand} onBack={() => setSelectedBrand(null)} />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Funnel size={16} className="text-slate-400" />
                  <BrandCombobox
                    value={clickBrand}
                    onChange={setClickBrand}
                    onSearch={searchReportBrands}
                    testId="click-brand-filter"
                  />
                  {discloseNetworks && (
                    <FeedPartnerSelect
                      value={networkFilter}
                      onChange={setNetworkFilter}
                      testId="click-network-filter"
                    />
                  )}
                  <DateRangePresets
                    preset={clickRange.preset}
                    start={clickRange.start}
                    end={clickRange.end}
                    onChange={setClickRange}
                    testId="click-date-range"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={exportClickLevelCsv} data-testid="download-click-csv">
                  <DownloadSimple size={16} className="mr-1.5" /> Download CSV
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                <StatCard label="Total Clicks" value={fmtInt(clickTotalClicks)} loading={loadingClicks && !clickData} />
                <StatCard label="Total Actions" value={fmtInt(clickTotalActions)} loading={loadingClicks && !clickData} />
                <StatCard label="Sale Amount" value={fmtMoney(clickTotalSaleAmount)} loading={loadingClicks && !clickData} />
                <StatCard label="Brands with Traffic" value={fmtInt(clickBrandCount)} loading={loadingClicks && !clickData} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <StatusEarningCard
                  label="Approved"
                  value={fmtMoney(clickApprovedEarning)}
                  sub={`${fmtInt(clickApprovedActions)} conv`}
                  tone="primary"
                  loading={loadingClicks && !clickData}
                  testId="click-tile-approved"
                />
                <StatusEarningCard
                  label="Pending"
                  value={fmtMoney(clickPendingEarning)}
                  sub={`${fmtInt(clickPendingActions)} conv`}
                  tone="pending"
                  loading={loadingClicks && !clickData}
                  testId="click-tile-pending"
                />
                <StatusEarningCard
                  label="Rejected"
                  value={fmtMoney(clickRejectedEarning)}
                  sub={`${fmtInt(clickRejectedActions)} conv`}
                  tone="rejected"
                  loading={loadingClicks && !clickData}
                  testId="click-tile-rejected"
                />
              </div>

              {/* Your SubID Click Data — single-brand only (keeps All Brands snappy) */}
              {showSubidPanel && (
                <div className="border border-[#002FA7]/20 bg-[#002FA7]/5 rounded-md p-5 mb-4">
                  <h3 className="text-xs uppercase tracking-wider text-[#002FA7] font-bold mb-3">Your SubID Tracked Clicks</h3>
                  <div className="space-y-2 max-h-[280px] overflow-auto">
                    {subidRows.map((r, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-[#002FA7]/10 last:border-0">
                        <span className="text-sm font-medium text-slate-900">{r.campaign_name}</span>
                        <div className="flex items-center gap-4 text-xs font-mono">
                          <span>Raw: {r.raw_clicks}</span>
                          <span>Unique: {r.clicks}</span>
                          <span>Actions: {r.actions}</span>
                          <span className="text-slate-700 font-bold">${(r.pub_commission || 0).toFixed(2)}</span>
                          <span className="text-[#002FA7] font-bold">${(r.earnings || 0).toFixed(2)}</span>
                          <span className="text-[#002FA7] font-bold">{(r.pub_commission_pct ?? r.commission_pct) || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loadingClicks && !clickData ? <TableSkeleton cols={discloseNetworks ? 11 : 10} rows={8} /> : (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="max-h-[500px] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="border-b border-slate-200">
                          <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                          {discloseNetworks && (
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold whitespace-nowrap min-w-[7.5rem]">Feed Partner</th>
                          )}
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Clicks</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Actions</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Impressions</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Sale Amount</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Total Commission</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Your Earning</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">CR</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">EPC</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Your %</th>
                          <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clickRows.map((r, i) => (
                          <tr key={`${r.campaign_name}-${i}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedBrand(r.campaign_name)} data-testid={`click-row-${r.campaign_name}`}>
                            <td className="px-4 py-2.5 font-medium text-slate-900">{r.campaign_name}</td>
                            {discloseNetworks && (
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                {r.network ? (
                                  <NetworkBadge network={r.network} />
                                ) : (
                                  <span className="text-xs text-slate-400">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">{r.clicks?.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-[#002FA7]">{r.actions}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-slate-500">{r.impressions}</td>
                            <td className="px-4 py-2.5 text-right font-mono">${(r.sale_amount || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-slate-700" data-testid={`click-row-commission-${r.campaign_name}`}>${(r.pub_commission || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-bold text-[#002FA7]" data-testid={`click-row-earnings-${r.campaign_name}`}>${(r.earnings || 0).toFixed(2)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-slate-600">{parseFloat(r.cr || 0).toFixed(4)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-slate-600">{parseFloat(r.epc || 0).toFixed(4)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-[#002FA7] font-semibold">{(r.pub_commission_pct ?? r.commission_pct) || 0}%</td>
                            <td className="px-4 py-2.5 text-right"><CursorClick size={14} className="text-slate-400" /></td>
                          </tr>
                        ))}
                        {clickRows.length === 0 && <tr><td colSpan={discloseNetworks ? 12 : 11} className="px-4 py-8 text-center text-sm text-slate-400">No click data available</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {clickLevelTotal > REPORT_PAGE_SIZE && (
                    <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
                      <span>
                        {clickLevelTotal.toLocaleString()} brands · page{" "}
                        <span className="font-mono font-bold">{clickLevelPageSafe}</span> /{" "}
                        <span className="font-mono">{clickLevelPages}</span>
                      </span>
                      <div className="flex gap-1.5">
                        <Button
                          variant="outline" size="sm"
                          disabled={clickLevelPageSafe <= 1 || loadingClicks}
                          onClick={() => setClickLevelPage(clickLevelPageSafe - 1)}
                          data-testid="click-level-prev"
                        >Prev</Button>
                        <Button
                          variant="outline" size="sm"
                          disabled={clickLevelPageSafe >= clickLevelPages || loadingClicks}
                          onClick={() => setClickLevelPage(clickLevelPageSafe + 1)}
                          data-testid="click-level-next"
                        >Next</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ═══ COMMISSION LEVEL ═══ */}
        <TabsContent value="commission-level">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Funnel size={16} className="text-slate-400" />
              <BrandCombobox
                value={commBrand}
                onChange={setCommBrand}
                onSearch={searchReportBrands}
                testId="comm-brand-filter"
              />
              {discloseNetworks && (
                <FeedPartnerSelect
                  value={networkFilter}
                  onChange={setNetworkFilter}
                  testId="comm-network-filter"
                />
              )}
              <DateRangePresets
                preset={commRange.preset}
                start={commRange.start}
                end={commRange.end}
                onChange={setCommRange}
                testId="comm-date-range"
              />
            </div>
            <Button
              variant="outline" size="sm" onClick={exportCommissionCsv}
              disabled={commCsvEstimate?.is_blocked}
              data-testid="download-comm-csv"
              title={commCsvEstimate?.is_blocked
                ? `Range too large (${commCsvEstimate.estimated_rows.toLocaleString()} rows). Narrow the date range.`
                : ""}
            >
              <DownloadSimple size={16} className="mr-1.5" />
              {commCsvEstimate
                ? `Download CSV (${commCsvEstimate.estimated_rows.toLocaleString()} ${commCsvEstimate.tier === "zip" ? "rows — ZIP" : commCsvEstimate.tier === "background" ? "rows — emailed" : "rows"})`
                : "Download CSV"}
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 mb-4">
            <StatCard label="Total Actions" value={fmtInt(commActionCount)} loading={loadingComm && !commData} />
            <StatCard label="Total Sale Amount" value={fmtMoney(commTotalAmount)} loading={loadingComm && !commData} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <StatusEarningCard
              label="Approved"
              value={fmtMoney(commApprovedEarning)}
              sub={`${fmtInt(commApprovedActions)} conv`}
              tone="primary"
              loading={loadingComm && !commData}
              testId="comm-tile-approved"
              active={commStatus === "approved"}
              onClick={() => toggleCommStatus("approved")}
            />
            <StatusEarningCard
              label="Pending"
              value={fmtMoney(commPendingEarning)}
              sub={`${fmtInt(commPendingActions)} conv`}
              tone="pending"
              loading={loadingComm && !commData}
              testId="comm-tile-pending"
              active={commStatus === "pending"}
              onClick={() => toggleCommStatus("pending")}
            />
            <StatusEarningCard
              label="Rejected"
              value={fmtMoney(commRejectedEarning)}
              sub={`${fmtInt(commRejectedActions)} conv`}
              tone="rejected"
              loading={loadingComm && !commData}
              testId="comm-tile-rejected"
              active={commStatus === "rejected"}
              onClick={() => toggleCommStatus("rejected")}
            />
          </div>

          {loadingComm && !commData ? <TableSkeleton cols={8} rows={8} /> : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="max-h-[500px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">State</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Amount</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Total Commission</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Commission %</th>
                      <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Your Earning</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Date</th>
                      <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Promo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commRows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-2.5 font-medium text-slate-900">{r.campaign_name}</td>
                        <td className="px-4 py-2.5"><StatusPill status={r.state} size="md" /></td>
                        <td className="px-4 py-2.5 text-right font-mono">${r.amount?.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-700">${(r.pub_commission ?? 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-[#002FA7]">{(r.pub_commission_pct ?? r.effective_rate_pct ?? r.commission_pct ?? 0).toFixed(2)}%</td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-[#002FA7]">${r.earning?.toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">{r.event_date?.substring(0, 10)}</td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">{r.promo_code || "-"}</td>
                      </tr>
                    ))}
                    {commRows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">
                          {commStatus
                            ? `No ${commStatus} commission data for selected filters`
                            : "No commission data for selected filters"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {commTotal > REPORT_PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
                  <span>
                    {commTotal.toLocaleString()} actions · page{" "}
                    <span className="font-mono font-bold">{commPageSafe}</span> /{" "}
                    <span className="font-mono">{commPages}</span>
                  </span>
                  <div className="flex gap-1.5">
                    <Button
                      variant="outline" size="sm"
                      disabled={commPageSafe <= 1 || loadingComm}
                      onClick={() => setCommPage(commPageSafe - 1)}
                      data-testid="comm-level-prev"
                    >Prev</Button>
                    <Button
                      variant="outline" size="sm"
                      disabled={commPageSafe >= commPages || loadingComm}
                      onClick={() => setCommPage(commPageSafe + 1)}
                      data-testid="comm-level-next"
                    >Next</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ═══ CLICK LOG ═══ */}
        <TabsContent value="conversion-log">
          <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
            <span className="text-base leading-none mt-0.5">⭐</span>
            <p className="text-xs text-amber-900 leading-relaxed">
              <b>Single source of truth.</b> The money cards are <b>net of Rejected</b> — the same basis as your Dashboard and the other report tabs, so every screen shows one number. <code className="bg-amber-100 px-1 rounded font-mono">Download CSV</code> carries <b>every</b> row including rejected ones, each labelled in its <b>Status</b> column: exclude <code className="bg-amber-100 px-1 rounded font-mono">Rejected</code> and the CSV reconciles with the cards penny-for-penny, or keep them and you get the gross figure. Money tiles filter on <b>conversion date</b> (when the sale closed). Total Clicks matches your tracker <code className="bg-amber-100 px-1 rounded font-mono">click_log</code> (plus true off-tracker conversions). Click / Conversion DateTime use your account timezone (date + time):{" "}
              <code className="bg-amber-100 px-1 rounded font-mono" data-testid="click-log-timezone-note">{accountTimezone}</code>
              {" "}(change in Profile).
            </p>
          </div>
          {(() => {
            const clickLogRows = convLogData?.data || [];
            // Prefer server-side totals (computed over the FULL filtered
            // set, not just the current page). Falls back to per-page
            // sums for backwards-compat with cached responses.
            const serverTotals = convLogData?.totals;
            const totalClicks = serverTotals?.total_clicks ?? clickLogRows.length;
            const totalSales = serverTotals?.total_sales ?? clickLogRows.filter(r => r.sales > 0).length;
            const totalSaleAmt = serverTotals?.total_sale_amount ?? clickLogRows.reduce((s, r) => s + r.sale_amount, 0);
            const approvedEarning = serverTotals?.approved_earning ?? 0;
            const pendingEarning = serverTotals?.pending_earning ?? 0;
            const rejectedEarning = serverTotals?.rejected_earning ?? 0;
            const approvedActions = serverTotals?.approved_actions ?? 0;
            const pendingActions = serverTotals?.pending_actions ?? 0;
            const rejectedActions = serverTotals?.rejected_actions ?? 0;
            const avgAOV = serverTotals?.avg_aov ?? (totalSales > 0 ? totalSaleAmt / totalSales : 0);
            const overallCR = serverTotals?.overall_cr ?? (totalClicks > 0 ? (totalSales / totalClicks * 100) : 0);
            // Clamp CR display to 100% — values > 100 mean the publisher
            // has more conversions than tracker-recorded clicks (typical
            // when pub uses network deep links that bypass /api/c), which
            // is not really "conversion rate" in the classic sense.
            const crDisplay = overallCR > 100 ? "100%+" : `${overallCR.toFixed(2)}%`;

            return (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Funnel size={16} className="text-slate-400" />
                    <BrandCombobox
                      value={convLogBrand}
                      onChange={setConvLogBrand}
                      onSearch={searchReportBrands}
                      testId="convlog-brand-filter"
                    />
                    {discloseNetworks && (
                      <FeedPartnerSelect
                        value={networkFilter}
                        onChange={setNetworkFilter}
                        testId="convlog-network-filter"
                      />
                    )}
                    <DateRangePresets
                      preset={convLogRange.preset}
                      start={convLogRange.start}
                      end={convLogRange.end}
                      onChange={setConvLogRange}
                      testId="convlog-date-range"
                    />
                    <Input
                      placeholder="Filter by SubId2 (your click ID)..."
                      value={subIdFilter}
                      onChange={(e) => setSubIdFilter(e.target.value)}
                      className="h-9 text-sm w-64"
                      data-testid="subid2-filter-input"
                    />
                    {subIdFilter && (
                      <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500" onClick={() => setSubIdFilter("")}>
                        Clear SubId2
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadClickLogCsv}
                    disabled={clickLogCsvExporting}
                    data-testid="download-convlog-csv"
                  >
                    {clickLogCsvExporting ? (
                      <><CircleNotch size={16} className="mr-1.5 animate-spin" /> Preparing…</>
                    ) : (
                      <><DownloadSimple size={16} className="mr-1.5" /> Download CSV</>
                    )}
                  </Button>
                </div>

                {clickLogCsvExporting && (
                  <div
                    className="mb-4 border border-slate-200 bg-slate-50 rounded-md p-3 flex items-center gap-3"
                    data-testid="convlog-csv-export-progress"
                  >
                    <CircleNotch size={18} className="text-[#002FA7] animate-spin shrink-0" />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-slate-700">
                        Downloading {clickLogCsvProgress.total > 0
                          ? <>~<span className="font-mono">{clickLogCsvProgress.total.toLocaleString()}</span> rows</>
                          : "click log"}…
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex gap-3">
                        <span>{(clickLogCsvProgress.received / 1024).toFixed(0)} KB received</span>
                        <span>{(clickLogCsvProgress.elapsedMs / 1000).toFixed(1)}s elapsed</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                  <StatCard label="Total Clicks" value={fmtInt(totalClicks)} loading={loadingConvLog && !convLogData} />
                  <StatCard label="Sales" value={fmtInt(totalSales)} loading={loadingConvLog && !convLogData} />
                  <StatCard label="Sale Amount" value={fmtMoney(totalSaleAmt)} loading={loadingConvLog && !convLogData} />
                  <StatCard label="Avg AOV" value={fmtMoney(avgAOV)} loading={loadingConvLog && !convLogData} />
                  <StatCard label="CR" value={crDisplay} loading={loadingConvLog && !convLogData} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  <StatusEarningCard
                    label="Approved"
                    value={fmtMoney(approvedEarning)}
                    sub={`${fmtInt(approvedActions)} conv`}
                    tone="primary"
                    loading={loadingConvLog && !convLogData}
                    testId="convlog-tile-approved"
                  />
                  <StatusEarningCard
                    label="Pending"
                    value={fmtMoney(pendingEarning)}
                    sub={`${fmtInt(pendingActions)} conv`}
                    tone="pending"
                    loading={loadingConvLog && !convLogData}
                    testId="convlog-tile-pending"
                  />
                  <StatusEarningCard
                    label="Rejected"
                    value={fmtMoney(rejectedEarning)}
                    sub={`${fmtInt(rejectedActions)} conv`}
                    tone="rejected"
                    loading={loadingConvLog && !convLogData}
                    testId="convlog-tile-rejected"
                  />
                </div>

                {loadingConvLog && !convLogData ? <TableSkeleton cols={9} rows={8} /> : (
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <div className="max-h-[500px] overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="border-b border-slate-200">
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">DateTime</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">RID (Click ID)</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">SubId2</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">SubId3</th>
                            <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Sales</th>
                            <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Sale Amt</th>
                            <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Total Commission</th>
                            <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Your Earning</th>
                            <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Rate</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Order ID</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Promo</th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clickLogRows.map((r, i) => (
                            <tr key={i} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${r.sales > 0 ? "bg-emerald-50/30" : ""}`}>
                              <td className="px-3 py-2 text-slate-900 text-xs">{r.date}</td>
                              <td className="px-3 py-2 font-mono text-[10px] text-[#002FA7] font-bold">{r.rid || (r.tracker_source === "off-tracker" ? "—" : "")}</td>
                              <td className="px-3 py-2 font-medium text-slate-900 text-xs">
                                <span>{r.brand}</span>
                                {r.tracker_source === "off-tracker" && (
                                  /* Conversion came back from the network's
                                     postback/API sync without a matching
                                     click_log row — pubs used a direct
                                     deep-link (Skimlinks, FlexOffers) or
                                     Impact deep-link that bypassed our
                                     /api/c tracker. Click count is implied
                                     from the conversion (1 minimum). */
                                  <span
                                    className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200"
                                    title={`Off-tracker — pulled from ${r.network || "network"} postback. The click count is a floor (≥1 per conversion).`}
                                    data-testid={`off-tracker-badge-${i}`}
                                  >off-tracker</span>
                                )}
                              </td>
                              <td className="px-3 py-2 font-mono text-[10px] text-slate-700" data-testid={`subid2-${i}`}>{r.sub_id2 || "-"}</td>
                              <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.sub_id3 || "-"}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{r.sales > 0 ? <span className="text-emerald-600 font-bold">{r.sales}</span> : "0"}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{r.sale_amount > 0 ? `$${r.sale_amount.toFixed(2)}` : "-"}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs text-slate-700">{(r.pub_commission || 0) > 0 ? `$${r.pub_commission.toFixed(2)}` : "-"}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-[#002FA7]">{(r.commission ?? r.earnings) > 0 ? `$${(r.commission ?? r.earnings).toFixed(2)}` : "-"}</td>
                              <td className="px-3 py-2 text-right font-mono text-[10px] text-slate-600">{((r.pub_commission || r.commission || r.earnings) > 0 && r.commission_pct > 0) ? `${r.commission_pct}%` : "-"}</td>
                              <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                              <td className="px-3 py-2 text-slate-600 text-[10px] font-mono">{r.order_id || "-"}</td>
                              <td className="px-3 py-2 text-slate-600 text-[10px] font-mono">{r.promo_code || "-"}</td>
                              <td className="px-3 py-2 text-slate-500 text-[10px] font-mono">{r.action_id || "-"}</td>
                            </tr>
                          ))}
                          {clickLogRows.length === 0 && <tr><td colSpan={14} className="px-4 py-8 text-center text-sm text-slate-400">No click data yet. Clicks will appear here as traffic flows through your tracking links.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                    {clickLogTotal > REPORT_PAGE_SIZE && (() => {
                      const totalPages = Math.ceil(clickLogTotal / REPORT_PAGE_SIZE);
                      const showingFrom = (clickLogPage - 1) * REPORT_PAGE_SIZE + 1;
                      const showingTo = Math.min(clickLogPage * REPORT_PAGE_SIZE, clickLogTotal);
                      return (
                        <div
                          className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between"
                          data-testid="click-log-pagination"
                        >
                          <div className="text-xs text-slate-500" data-testid="click-log-page-info">
                            Showing <span className="font-mono font-semibold text-slate-700">{showingFrom.toLocaleString()}–{showingTo.toLocaleString()}</span> of <span className="font-mono font-semibold text-slate-700">{clickLogTotal.toLocaleString()}</span> clicks
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline" size="sm" disabled={clickLogPage <= 1 || loadingConvLog}
                              onClick={() => setClickLogPage(p => Math.max(1, p - 1))}
                              data-testid="click-log-prev-page"
                            >
                              Prev
                            </Button>
                            <span className="text-xs text-slate-500 font-mono px-2">Page {clickLogPage} / {totalPages}</span>
                            <Button
                              variant="outline" size="sm" disabled={clickLogPage >= totalPages || loadingConvLog}
                              onClick={() => setClickLogPage(p => Math.min(totalPages, p + 1))}
                              data-testid="click-log-next-page"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            );
          })()}
        </TabsContent>

        {/* ═══ EARNINGS BREAKDOWN TAB ═══ */}
        <TabsContent value="earnings-breakdown">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <BrandCombobox
                value={earningsBrand}
                onChange={setEarningsBrand}
                onSearch={searchReportBrands}
                testId="earnings-brand-select"
                placeholder="All brands"
              />
              {discloseNetworks && (
                <FeedPartnerSelect
                  value={networkFilter}
                  onChange={setNetworkFilter}
                  testId="earnings-network-filter"
                />
              )}
              <DateRangePresets
                preset={payoutRange.preset}
                start={payoutRange.start}
                end={payoutRange.end}
                onChange={setPayoutRange}
                testId="payout-date-range"
              />
            </div>
            {(payoutCsvEstimate?.estimated_rows ?? 0) > 0 && (
              <Button
                size="sm" variant="outline" onClick={exportEarningsCsv}
                disabled={payoutCsvEstimate?.is_blocked}
                data-testid="export-earnings-csv-btn"
                title={payoutCsvEstimate?.is_blocked
                  ? `Range too large (${payoutCsvEstimate.estimated_rows.toLocaleString()} rows). Narrow the date range.`
                  : ""}
              >
                <DownloadSimple size={14} className="mr-1.5" />
                {`Export (${payoutCsvEstimate.estimated_rows.toLocaleString()} ${payoutCsvEstimate.tier === "zip" ? "rows — ZIP" : payoutCsvEstimate.tier === "background" ? "rows — emailed" : "rows"})`}
              </Button>
            )}
          </div>

          {/* Summary tiles — status-segregated */}
          {(loadingEarnings && !earningsData?.totals) ? (
            <div className="space-y-3 mb-5">
              <div className="grid grid-cols-2 gap-3">
                {["Total Sales", "Conversions"].map((label) => (
                  <div key={label} className="border border-slate-200 rounded-md p-3 sm:p-4 bg-white min-w-0 overflow-hidden">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1 text-slate-500">{label}</div>
                    <Skeleton className="h-7 w-24 bg-slate-200" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {["Approved", "Pending", "Rejected"].map((label) => (
                  <div key={label} className="border border-slate-200 rounded-md p-3 sm:p-4 bg-white min-w-0 overflow-hidden">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1 text-slate-500">{label}</div>
                    <Skeleton className="h-7 w-24 bg-slate-200" />
                  </div>
                ))}
              </div>
            </div>
          ) : earningsData?.totals && (
            <div className="space-y-3 mb-5">
              <div className="grid grid-cols-2 gap-3">
                <StatusEarningCard
                  label="Total Sales"
                  value={fmtMoney(earningsData.totals.total_sale_amount)}
                  tone="neutral"
                  testId="earnings-tile-total-sales"
                />
                <StatusEarningCard
                  label="Conversions"
                  value={fmtInt(earningsData.totals.total_conversions)}
                  tone="neutral"
                  testId="earnings-tile-conversions"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatusEarningCard
                  label="Approved"
                  value={fmtMoney(earningsData.totals.approved_earning)}
                  sub={`${fmtInt(earningsData.totals.approved_actions)} conv`}
                  tone="primary"
                  testId="earnings-tile-approved"
                />
                <StatusEarningCard
                  label="Pending"
                  value={fmtMoney(earningsData.totals.pending_earning)}
                  sub={`${fmtInt(earningsData.totals.pending_actions)} conv`}
                  tone="pending"
                  testId="earnings-tile-pending"
                />
                <StatusEarningCard
                  label="Rejected"
                  value={fmtMoney(earningsData.totals.rejected_earning)}
                  sub={`${fmtInt(earningsData.totals.rejected_actions)} conv`}
                  tone="rejected"
                  testId="earnings-tile-rejected"
                />
              </div>
            </div>
          )}

          {/* Date-wise breakdown */}
          {(earningsData?.by_date?.length > 0) && (
            <div className="border border-slate-200 rounded-md overflow-hidden mb-5">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Earnings by Date</h4>
              </div>
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Date</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Sale</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-emerald-700 font-bold">Approved</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-amber-700 font-bold">Pending</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-red-700 font-bold">Rejected</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Conv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsData.by_date.map((r) => (
                      <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.label}</td>
                        <td className="px-4 py-2 text-right font-mono text-slate-700">{fmtMoney(r.sale_amount)}</td>
                        <td className="px-4 py-2 text-right font-mono text-emerald-700 font-semibold">{fmtMoney(r.approved?.pub_earning)}</td>
                        <td className="px-4 py-2 text-right font-mono text-amber-700">{fmtMoney(r.pending?.pub_earning)}</td>
                        <td className="px-4 py-2 text-right font-mono text-red-700">{fmtMoney(r.rejected?.pub_earning)}</td>
                        <td className="px-4 py-2 text-right font-mono text-slate-600">{fmtInt(r.actions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Brand-wise breakdown */}
          {(earningsData?.by_brand?.length > 0) && (
            <div className="border border-slate-200 rounded-md overflow-hidden mb-5">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Earnings by Brand</h4>
              </div>
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Brand</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Sale</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-emerald-700 font-bold">Approved</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-amber-700 font-bold">Pending</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-red-700 font-bold">Rejected</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Conv</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsData.by_brand.map((r) => (
                      <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="px-4 py-2 font-medium text-slate-900">{r.label}</td>
                        <td className="px-4 py-2 text-right font-mono text-slate-700">{fmtMoney(r.sale_amount)}</td>
                        <td className="px-4 py-2 text-right font-mono text-emerald-700 font-semibold">{fmtMoney(r.approved?.pub_earning)}</td>
                        <td className="px-4 py-2 text-right font-mono text-amber-700">{fmtMoney(r.pending?.pub_earning)}</td>
                        <td className="px-4 py-2 text-right font-mono text-red-700">{fmtMoney(r.rejected?.pub_earning)}</td>
                        <td className="px-4 py-2 text-right font-mono text-slate-600">{fmtInt(r.actions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loadingEarnings && !earningsData ? (
            <TableSkeleton cols={8} rows={8} />
          ) : !earningsData?.data?.length ? (
            <div className="border border-slate-200 rounded-md p-12 text-center">
              <p className="text-sm text-slate-500">No conversions yet for this filter.</p>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Per-Conversion Earnings</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/40">
                      <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Date</th>
                      <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Brand</th>
                      <th className="text-left px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Order</th>
                      <th className="text-center px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Status</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Sale</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Total Commission</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Rate</th>
                      <th className="text-right px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Your Earning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsData.data.map((r, i) => {
                      const st = (r.status || "").toUpperCase();
                      const earningTone =
                        st === "APPROVED" || st === "LOCKED" ? "text-emerald-700"
                        : st === "REVERSED" ? "text-red-700"
                        : "text-amber-700";
                      return (
                        <tr key={`${r.action_id}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="px-4 py-2 font-mono text-xs text-slate-600">{r.date}</td>
                          <td className="px-4 py-2 font-medium text-slate-900">{r.brand}</td>
                          <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.order_id || "—"}</td>
                          <td className="px-4 py-2 text-center">
                            <StatusPill status={r.status} size="md" />
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-slate-700">${(r.sale_amount || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono text-slate-700">${(r.pub_commission || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono text-slate-600">{(r.pub_commission_pct ?? r.effective_rate_pct ?? r.brand_commission_pct ?? 0).toFixed(2)}%</td>
                          <td className={`px-4 py-2 text-right font-mono font-bold ${earningTone}`}>${(r.pub_earning || 0).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {earningsTotal > REPORT_PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
                  <span>
                    {earningsTotal.toLocaleString()} conversions · page{" "}
                    <span className="font-mono font-bold">{earningsPageSafe}</span> /{" "}
                    <span className="font-mono">{earningsPages}</span>
                  </span>
                  <div className="flex gap-1.5">
                    <Button
                      variant="outline" size="sm"
                      disabled={earningsPageSafe <= 1 || loadingEarnings}
                      onClick={() => setEarningsPage(earningsPageSafe - 1)}
                      data-testid="earnings-prev"
                    >Prev</Button>
                    <Button
                      variant="outline" size="sm"
                      disabled={earningsPageSafe >= earningsPages || loadingEarnings}
                      onClick={() => setEarningsPage(earningsPageSafe + 1)}
                      data-testid="earnings-next"
                    >Next</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
